import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CustomerFieldsForm } from "@/components/CustomerFieldsForm";
import SSNRevealModal from "@/components/SSNRevealModal";
import { FmcsaLookupCard } from "@/components/FmcsaLookupCard";
import { ImportRentalDialog } from "@/components/ImportRentalDialog";
import { CreateRentalContractDialog } from "@/components/CreateRentalContractDialog";
import { CreateAchContractDialog } from "@/components/CreateAchContractDialog";
import SendAchActivationButton from "@/components/SendAchActivationButton";
import { formatRateChip } from "@/lib/rentalPricing";
import { saferRiskSummary, fmtFetchedAt } from "@/lib/saferRisk";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/lib/usePermission";
import { toast } from "sonner";
import { ChevronLeft, Send, Archive, ArchiveRestore, Save, FileText, Users, Paperclip, AlertTriangle, ShieldAlert, RefreshCcw, ShieldCheck, Loader2, Mail } from "lucide-react";
import { LogInAsButton } from "@/components/LogInAsButton";
import CustomerInvoicesTab from "@/components/CustomerInvoicesTab";
import CreateStatementModal from "@/components/CreateStatementModal";
import CustomerBillingModeCard from "@/components/CustomerBillingModeCard";
// Phase M.1 (2026-08-21) — surfaces a warning banner when the admin
// tries to reuse an equipment unit_number that a live rental already
// holds. Also drives the ``force=true`` + ``override_reason`` submit
// path for the attach-rental dialog.
import UnitNumberConflictBanner from "@/components/UnitNumberConflictBanner";
import {
    formatStatementIdentifier, formatStatementPeriod,
} from "@/lib/statementDisplay";
// Phase TOLLBILL-P3b → P5d (2026-07-10) — Customer Tolls tab (bills-first) + markup override field.
import TollBillsList from "@/components/TollBillsList";
import CustomerTollMarkupField from "@/components/CustomerTollMarkupField";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { FileMissingPostDeployModal } from "@/components/FileMissingPostDeployModal";  // Task 18 Phase F
import { Eye } from "lucide-react";
import { PortalInviteHeaderControl, PortalAccessTab, usePortalInvites } from "@/components/PortalInviteControls";
import { FmcsaSnapshotPanel } from "@/components/FmcsaSnapshotPanel";
import { CollapsibleSection } from "@/components/CollapsibleSection";

const EQUIPMENT_LABELS = {
    reefer: "Reefer", dry_van: "Dry Van", conestoga: "Conestoga",
    flatbed: "Flatbed", step_deck: "Step Deck", other: "Other",
};

const TABS = [
    { key: "profile", label: "Profile" },
    { key: "uploads", label: "Uploads" },
    { key: "submissions", label: "Submissions" },
    { key: "contracts", label: "Contracts" },
    { key: "rentals", label: "Rentals" },
    { key: "invoices", label: "Invoices" },
    { key: "statements", label: "Statements" },
    { key: "tolls", label: "Tolls" },
    { key: "users", label: "Linked users" },
    { key: "portal_access", label: "Portal access" },
];

const CONTRACT_STATUS_COLOR = {
    draft: "bg-slate-100 text-slate-700",
    sent_for_signature: "bg-blue-50 text-blue-900",
    customer_signed: "bg-amber-50 text-amber-900",
    fully_signed: "bg-emerald-50 text-emerald-800",
    cancelled: "bg-red-50 text-red-700",
    imported: "bg-violet-50 text-violet-800",
};

const CONTRACT_STATUS_LABEL = {
    draft: "Draft",
    sent_for_signature: "Sent",
    customer_signed: "Customer signed",
    fully_signed: "Fully signed",
    cancelled: "Cancelled",
    imported: "Imported",
};

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
}

export default function AdminCustomerDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user: currentUser } = useAuth();
    const isSuperAdmin = currentUser?.role === "super_admin";
    // Phase K.7.1 — admin role now also edits billing settings + reaches
    // Statements list; kept isSuperAdmin for older per-flow guards.
    const isAdmin = currentUser?.role === "admin";
    const [ssnRevealOpen, setSsnRevealOpen] = useState(false);
    const urlTab = new URLSearchParams(location.search).get("tab");
    const [c, setC] = useState(null);
    const [data, setData] = useState({});
    // Phase QCM-EXT followup — imperative handle for the FMCSA SAFER
    // lookup card so the CollapsibleSection wrapper can render a
    // "Re-verify with FMCSA" button in the header that stays usable
    // when the section is collapsed.
    const fmcsaLookupRef = useRef(null);
    // Tick to force-remount the FmcsaLookupCard's loading state into a
    // re-render after a header-triggered refresh.
    const [, setFmcsaLookupTick] = useState(0);
    // Phase QCM-EIN regression fix — eagerly fetched FMCSA QCM snapshot
    // for this customer's USDOT. Lifted into the page (away from the
    // SAFER/Snapshot child components) so the "Verified from FMCSA"
    // badge in the Company section's EIN field renders correctly even
    // when both FMCSA sections are collapsed.
    //
    // The endpoint is cached server-side (24h), so this fetch is
    // effectively free on revisits. Refresh on USDOT change.
    const [fmcsaQcmSnapshot, setFmcsaQcmSnapshot] = useState(null);
    const [tab, setTab] = useState(urlTab && TABS.some((t) => t.key === urlTab) ? urlTab : "profile");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Phase TOLLBILL-P3b — filter Tolls tab by permission.
    const { hasPermission: mainHasPermission } = usePermission();
    const visibleTabs = TABS.filter((t) => {
        if (t.key === "tolls") return mainHasPermission("Tolls", "View");
        return true;
    });
    const [error, setError] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    // Task 18 Phase F — reupload-reminder modal state. `null` = closed.
    // When opened, carries the file refs to email about.
    const [showReuploadModal, setShowReuploadModal] = useState(null);
    const [inviteName, setInviteName] = useState("");
    const [inviteEquipment, setInviteEquipment] = useState("");
    // Phase O.2 — read-only preview for customer-level uploads.
    const [previewFile, setPreviewFile] = useState(null);

    // Phase CINV — portal-invite state shared by header chip and Portal access tab.
    const { invites, loading: invitesLoading, reload: reloadInvites } = usePortalInvites(id);

    // Phase QCM-EIN regression fix — eagerly fetch the FMCSA QCM
    // snapshot for the current USDOT so the Company-section EIN
    // verified-badge can render even with the SAFER + Snapshot
    // sections collapsed. Silent on errors / no-data so a missing
    // snapshot doesn't pollute the page UX.
    useEffect(() => {
        let cancelled = false;
        const dot = (data.usdot_number || "").trim();
        if (!dot) {
            setFmcsaQcmSnapshot(null);
            return undefined;
        }
        api.get(`/admin/fmcsa/snapshot/${encodeURIComponent(dot)}`)
            .then(({ data: snap }) => {
                if (!cancelled) setFmcsaQcmSnapshot(snap || null);
            })
            .catch(() => {
                if (!cancelled) setFmcsaQcmSnapshot(null);
            });
        return () => { cancelled = true; };
    }, [data.usdot_number]);

    // Keep URL ?tab= in sync with the active tab so deep-links + filters work.
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        if (q.get("tab") !== tab) {
            q.set("tab", tab);
            navigate({ pathname: location.pathname, search: `?${q.toString()}` }, { replace: true });
        }
    }, [tab]);

    const load = async () => {
        setLoading(true);
        try {
            const { data: c } = await api.get(`/admin/customers/${id}`);
            setC(c);
            setData(c);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            ["company_name","dba","company_address","ein_tax_id","usdot_number","company_phone",
             "billing_contact_name","billing_contact_email","billing_contact_phone",
             "guarantor_name","guarantor_ssn","guarantor_home_address","guarantor_dob",
             "guarantor_phone","guarantor_email",
             "agent_name","agent_company","agent_email","agent_phone",
             "trade_reference_1","trade_reference_2","trade_reference_3","bank_reference"].forEach(k => {
                payload[k] = data?.[k] ?? null;
            });
            const { data: updated } = await api.patch(`/admin/customers/${id}`, payload);
            toast.success("Customer updated");
            setC(updated);
            setData(updated);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onArchive = async () => {
        if (!window.confirm("Archive this customer?")) return;
        try {
            await api.delete(`/admin/customers/${id}`);
            toast.success("Customer archived");
            navigate("/admin/customers");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onRestore = async () => {
        try {
            const { data: updated } = await api.post(`/admin/customers/${id}/restore`);
            toast.success("Customer restored");
            setC(updated);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onSendInvite = async () => {
        if (!inviteEmail.trim()) { toast.error("Email is required"); return; }
        try {
            const { data: inv } = await api.post(`/admin/customers/${id}/invite`, {
                customer_email: inviteEmail,
                customer_name: inviteName || c?.company_name || "Customer",
                field_config: {},
                expires_in_days: 7,
                equipment_type: inviteEquipment || null,
            });
            toast.success("Invite sent");
            setShowInvite(false);
            setInviteEmail("");
            setInviteName("");
            setInviteEquipment("");
            navigate(`/admin/invites?highlight=${inv.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const [reverifying, setReverifying] = useState(false);
    const onReverifySafer = async () => {
        setReverifying(true);
        try {
            const { data: payload } = await api.post(`/admin/customers/${id}/verify-safer`);
            setC((prev) => prev ? { ...prev, safer_snapshot: payload.safer_snapshot } : prev);
            setData((prev) => ({ ...prev, safer_snapshot: payload.safer_snapshot }));
            if (payload.safer_snapshot?.has_risk_flag) {
                toast.error("SAFER risk flag still present");
            } else if (payload.safer_snapshot?.error) {
                toast.error("SAFER still unavailable — try again later");
            } else {
                toast.success("SAFER re-verified — no risk flags");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setReverifying(false);
        }
    };

    if (loading) return (<DashboardShell><p className="text-sm text-slate-500" data-testid="customer-loading">Loading…</p></DashboardShell>);
    if (error || !c) return (
        <DashboardShell>
            <div className="rounded-md bg-red-50 border border-red-100 p-4 text-sm text-red-700" data-testid="customer-error">{error || "Not found"}</div>
            <Button asChild variant="outline" className="mt-4"><Link to="/admin/customers">Back</Link></Button>
        </DashboardShell>
    );

    return (
        <DashboardShell>
            <div className="flex items-start justify-between flex-wrap gap-3 fade-up">
                <div>
                    <Link to="/admin/customers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                        <ChevronLeft size={14} /> Customers
                    </Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                        Customer
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {c.company_name || "(no name)"}
                    </h1>
                    <p className="mt-2 text-sm text-slate-600">
                        Status: <span className={c.status === "archived" ? "text-amber-700" : "text-emerald-700"}>{c.status}</span>
                        {c.last_application_at && <> · Last applied {fmtDate(c.last_application_at)}</>}
                    </p>
                </div>
                <div className="flex gap-2 items-start">
                    {c.status === "active" && (
                        <PortalInviteHeaderControl
                            customer={c}
                            invites={invites}
                            onChange={reloadInvites}
                        />
                    )}
                    {c.status === "active" ? (
                        <>
                            <LogInAsButton
                                customerId={id}
                                targetRole="customer"
                                labelOverride="Log in as customer"
                                dataTestid="customer-header-login-as"
                            />
                            <Button onClick={() => setShowInvite(true)} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="customer-send-invite">
                                <Send size={14} /> Send submission invite
                            </Button>
                            {/* Phase J.4a (2026-07-29) — activation link */}
                            <SendAchActivationButton customer={c} onSent={load} />
                            <Button variant="outline" onClick={onArchive} data-testid="customer-archive" className="gap-1.5">
                                <Archive size={14} /> Archive
                            </Button>
                        </>
                    ) : (
                        <Button onClick={onRestore} variant="outline" className="gap-1.5" data-testid="customer-restore">
                            <ArchiveRestore size={14} /> Restore
                        </Button>
                    )}
                </div>
            </div>

            {(() => {
                const riskSummary = saferRiskSummary(c.safer_snapshot);
                if (!riskSummary) return null;
                const snap = c.safer_snapshot;
                if (riskSummary.kind === "risk") {
                    return (
                        <div
                            className="mt-5 rounded-md border border-red-200 bg-red-50 p-4 flex items-start gap-3"
                            data-testid="safer-risk-banner"
                        >
                            <ShieldAlert size={20} className="shrink-0 mt-0.5 text-red-700" />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-red-900">FMCSA SAFER risk flag</p>
                                <ul className="mt-1 list-disc ml-5 text-sm text-red-800 space-y-0.5">
                                    {riskSummary.messages.map((m, i) => (
                                        <li key={i} data-testid={`safer-risk-reason-${riskSummary.reasons[i]}`}>{m}</li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-xs text-red-800/80">
                                    Last re-verified: {fmtFetchedAt(snap?.fetched_at)}
                                    {snap?.out_of_service_date && ` · Out-of-service since ${snap.out_of_service_date}`}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onReverifySafer}
                                disabled={reverifying}
                                data-testid="safer-reverify"
                                className="gap-1.5 shrink-0"
                            >
                                <RefreshCcw size={14} /> {reverifying ? "Re-verifying…" : "Re-verify now"}
                            </Button>
                        </div>
                    );
                }
                if (riskSummary.kind === "unavailable") {
                    return (
                        <div
                            className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                            data-testid="safer-unavailable-banner"
                        >
                            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-800" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-amber-900 font-medium">SAFER verification didn't complete</p>
                                <p className="text-xs text-amber-900/80 mt-0.5">Click Re-verify to try again.</p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onReverifySafer}
                                disabled={reverifying}
                                data-testid="safer-reverify"
                                className="gap-1.5 shrink-0"
                            >
                                <RefreshCcw size={14} /> {reverifying ? "Re-verifying…" : "Re-verify now"}
                            </Button>
                        </div>
                    );
                }
                return null;
            })()}

            {showInvite && (
                <Card className="mt-6" data-testid="customer-invite-form">
                    <CardContent className="p-5 space-y-4">
                        <h3 className="font-display text-base font-semibold text-slate-900">Send a returning-customer invite</h3>
                        <p className="text-xs text-slate-500">The customer will see "Use previous info?" prompt and can prefill from this profile.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="inv-email">Email</Label>
                                <Input id="inv-email" data-testid="invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="inv-name">Customer name (optional)</Label>
                                <Input id="inv-name" data-testid="invite-name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label htmlFor="inv-eq">Pre-select equipment type (optional)</Label>
                                <select id="inv-eq" data-testid="invite-equipment" value={inviteEquipment} onChange={(e) => setInviteEquipment(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                                    <option value="">No preselect — customer picks</option>
                                    {Object.entries(EQUIPMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setShowInvite(false)} data-testid="invite-cancel">Cancel</Button>
                            <Button onClick={onSendInvite} className="bg-blue-900 hover:bg-blue-800" data-testid="invite-send">
                                Send invite
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <ol className="mt-6 flex flex-wrap gap-2 text-xs" data-testid="customer-tabs">
                {visibleTabs.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        data-testid={`customer-tab-${t.key}`}
                        className={`px-3 py-1.5 rounded-full font-medium transition-colors ${
                            t.key === tab ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                    >{t.label}</button>
                ))}
            </ol>

            <Card className="mt-4">
                <CardContent className="p-6">
                    {tab === "profile" && (
                        <div className="space-y-6">
                            {/* Phase QCM-EXT followup — both FMCSA panels
                                live ABOVE the company-info form.
                                CollapsibleSection wraps the SAFER
                                verification card; the FmcsaSnapshotPanel
                                has its own internal collapse. Both default
                                to collapsed; localStorage keys persist
                                each user's choice. */}
                            {data.usdot_number && (
                                <CollapsibleSection
                                    title="FMCSA SAFER Verification"
                                    storageKey="admin_fmcsa_safer_collapsed"
                                    defaultCollapsed={true}
                                    testIdPrefix="admin-fmcsa-safer"
                                    headerExtras={(
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                fmcsaLookupRef.current?.refresh();
                                                // Bump local tick so the
                                                // header button reflects
                                                // the in-flight loading
                                                // state on next paint.
                                                setFmcsaLookupTick((n) => n + 1);
                                            }}
                                            disabled={!fmcsaLookupRef.current?.isValid || fmcsaLookupRef.current?.isLoading}
                                            className="gap-1.5"
                                            data-testid="admin-fmcsa-safer-header-reverify"
                                        >
                                            {fmcsaLookupRef.current?.isLoading
                                                ? <Loader2 size={14} className="animate-spin" />
                                                : <ShieldCheck size={14} />}
                                            {fmcsaLookupRef.current?.isLoading
                                                ? "Verifying…"
                                                : (fmcsaLookupRef.current?.hasSnapshot ? "Re-verify with FMCSA" : "Verify with FMCSA")}
                                        </Button>
                                    )}
                                >
                                    <FmcsaLookupCard
                                        ref={fmcsaLookupRef}
                                        usdot={data.usdot_number}
                                        companyValues={{
                                            company_name: data.company_name || "",
                                            company_address: data.company_address || "",
                                            company_phone: data.company_phone || "",
                                            ein_tax_id: data.ein_tax_id || "",
                                        }}
                                        onPrefill={(patch) => setData((d) => ({ ...d, ...patch }))}
                                        testIdPrefix="customer-edit-fmcsa"
                                    />
                                </CollapsibleSection>
                            )}
                            {/* FMCSA Snapshot panel — uses its own
                                internal collapse via the `collapsible`
                                prop because its existing CardHeader
                                already hosts the Refresh button + the
                                "Sourced …" subline. */}
                            <FmcsaSnapshotPanel
                                dotNumber={data.usdot_number || null}
                                onApplyEin={(ein) => setData((d) => ({ ...d, ein_tax_id: ein }))}
                                collapsible
                                storageKey="admin_fmcsa_snapshot_collapsed"
                                defaultCollapsed={true}
                            />
                            <CustomerFieldsForm
                                data={data}
                                onChange={setData}
                                prefix="customer-edit"
                                fmcsaQcm={fmcsaQcmSnapshot}
                                isSuperAdmin={isSuperAdmin}
                                onRevealSsn={() => setSsnRevealOpen(true)}
                            />
                            <div className="flex gap-2 justify-end">
                                <Button onClick={onSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="customer-save">
                                    <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {tab === "uploads" && (
                        <div className="space-y-3" data-testid="customer-uploads-list">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Uploads on file</h3>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => load()}
                                    disabled={loading}
                                    data-testid="customer-uploads-refresh"
                                    className="gap-1.5 text-xs"
                                >
                                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                                </Button>
                            </div>
                            {/* Task 18 Phase F — count uploads whose file is gone post-deploy. */}
                            {(() => {
                                const missing = (c.uploads || []).filter(u => u.missing_post_deploy === true);
                                if (missing.length === 0) return null;
                                return (
                                    <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="customer-uploads-missing-banner">
                                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="font-medium">
                                                {missing.length} file{missing.length === 1 ? "" : "s"} unavailable post-migration.
                                            </p>
                                            <p className="text-[11px] mt-0.5">Some uploads were stored on a previous deployment and didn't survive the storage migration. Ask the customer to re-upload.</p>
                                        </div>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => setShowReuploadModal({
                                                customerId: c.id,
                                                filename: missing.length === 1 ? missing[0].filename : `${missing.length} files`,
                                                fileRefs: missing.map(u => ({ label: u.filename || u.field_key, kind: u.field_key })),
                                            })}
                                            className="bg-blue-900 hover:bg-blue-800 gap-1.5 text-xs"
                                            data-testid="customer-uploads-reupload-cta"
                                        >
                                            <Mail size={12} /> Email customer to re-upload
                                        </Button>
                                    </div>
                                );
                            })()}
                            {c.uploads.length === 0 && (() => {
                                const subs = c.submissions || [];
                                const hasApproved = subs.some(s => ["approved", "equipment_assigned", "awaiting_reassignment"].includes(s.status));
                                const hasPending = subs.some(s => ["submitted", "pending_review"].includes(s.status));
                                if (hasApproved) {
                                    return (
                                        <p className="text-sm text-amber-700" data-testid="customer-uploads-empty-approved">
                                            No uploads on file yet. This customer has an approved submission — try Refresh; if it&apos;s still empty, the auto-copy step may not have completed.
                                        </p>
                                    );
                                }
                                if (hasPending) {
                                    return (
                                        <p className="text-sm text-slate-500" data-testid="customer-uploads-empty-pending">
                                            No uploads stored yet. Uploads will be auto-copied here once the pending submission is approved.
                                        </p>
                                    );
                                }
                                return <p className="text-sm text-slate-500" data-testid="customer-uploads-empty">No uploads stored.</p>;
                            })()}
                            {c.uploads.length > 0 && (
                                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
                                    {c.uploads.map((u) => (
                                        <li key={u.id} className="px-4 py-3 flex justify-between items-center gap-3" data-testid={`customer-upload-${u.field_key}`}>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <Paperclip size={16} className="text-slate-400 shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="font-medium text-sm text-slate-900 truncate">{u.filename}</div>
                                                    <div className="text-xs text-slate-500">{u.field_key} · uploaded {fmtDate(u.uploaded_at)}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className="text-xs text-slate-500">{u.size ? `${(u.size/1024).toFixed(1)} KB` : "—"}</span>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setPreviewFile(u)}
                                                    data-testid={`customer-preview-upload-${u.field_key}`}
                                                    className="gap-1.5"
                                                >
                                                    <Eye size={12} /> Preview
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="text-xs text-slate-500">Uploads are auto-copied from the most recent approved submission. To replace, ask the customer to re-upload via a new application.</p>
                        </div>
                    )}

                    {tab === "submissions" && (
                        <div data-testid="customer-submissions-list">
                            <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">Submission history</h3>
                            {(c.submissions || []).length === 0 && <p className="text-sm text-slate-500">No submissions yet.</p>}
                            {(c.submissions || []).length > 0 && (
                                <table className="min-w-full text-sm">
                                    <thead className="text-xs text-slate-500 uppercase">
                                        <tr><th className="text-left py-2">Equipment</th><th className="text-left py-2">Status</th><th className="text-left py-2">Submitted</th><th className="text-left py-2">Reviewed</th><th></th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {c.submissions.map((s) => (
                                            <tr key={s.id} data-testid={`customer-sub-row-${s.id}`}>
                                                <td className="py-2">{EQUIPMENT_LABELS[s.equipment_type] || s.equipment_type_other || s.equipment_type || "—"}</td>
                                                <td className="py-2 capitalize">{s.status}</td>
                                                <td className="py-2 text-slate-600">{fmtDate(s.submitted_at)}</td>
                                                <td className="py-2 text-slate-600">{fmtDate(s.reviewed_at)}</td>
                                                <td className="py-2 text-right">
                                                    <Button asChild size="sm" variant="outline">
                                                        <Link to={`/admin/submissions/${s.id}`} data-testid={`customer-sub-open-${s.id}`}>Open</Link>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {tab === "contracts" && (
                        <ContractsTab customerId={id} customerName={c.company_name} />
                    )}

                    {tab === "rentals" && (
                        <RentalsTab customerId={id} />
                    )}

                    {tab === "invoices" && (
                        <CustomerInvoicesTab customerId={id} customerName={c.company_name} />
                    )}

                    {tab === "statements" && (
                        <CustomerStatementsTab
                            customerId={id}
                            customer={c}
                            isSuperAdmin={isSuperAdmin}
                            isAdmin={isAdmin}
                            onCustomerRefresh={load}
                        />
                    )}

                    {tab === "tolls" && (
                        <div className="space-y-4" data-testid="customer-tolls-tab">
                            <div>
                                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">Toll bills</h3>
                                <TollBillsList
                                    customerId={id}
                                    testIdPrefix="customer-tolls"
                                    emptyText="No toll bills have been recorded for this customer yet."
                                />
                            </div>
                            <div>
                                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">Markup</h3>
                                <CustomerTollMarkupField
                                    customer={c}
                                    onSaved={(newVal) => setC((prev) => (prev ? { ...prev, toll_markup_percent: newVal } : prev))}
                                />
                            </div>
                        </div>
                    )}

                    {tab === "users" && (
                        <div data-testid="customer-users-list">
                            <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-3">Linked customer users</h3>
                            {(c.linked_users || []).length === 0 && <p className="text-sm text-slate-500">No linked users yet.</p>}
                            {(c.linked_users || []).length > 0 && (
                                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
                                    {c.linked_users.map((u) => (
                                        <li key={u.id} className="px-4 py-3 flex justify-between items-center gap-3" data-testid={`customer-user-${u.id}`}>
                                            <div>
                                                <div className="font-medium text-sm text-slate-900">{u.full_name}</div>
                                                <div className="text-xs text-slate-500">{u.email}</div>
                                            </div>
                                            <Users size={14} className="text-slate-400" />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="mt-3 text-xs text-slate-500">Linked users are added automatically when an admin approves their submission for this customer.</p>
                        </div>
                    )}

                    {tab === "portal_access" && (
                        <PortalAccessTab
                            customer={c}
                            invites={invites}
                            loading={invitesLoading}
                            onChange={async () => { await reloadInvites(); await load(); }}
                        />
                    )}
                </CardContent>
            </Card>

            <FilePreviewModal
                open={!!previewFile}
                onOpenChange={(o) => !o && setPreviewFile(null)}
                file={previewFile}
                downloadUrl={
                    previewFile && c
                        ? `/admin/customers/${c.id}/uploads/${previewFile.id}`
                        : null
                }
                mode="customer"
            />

            <SSNRevealModal
                open={ssnRevealOpen}
                onOpenChange={setSsnRevealOpen}
                customerId={id}
                customerName={c?.company_name || data?.company_name || null}
            />

            {/* Task 18 Phase F — reupload-reminder modal for files lost in the storage migration */}
            <FileMissingPostDeployModal
                open={!!showReuploadModal}
                onOpenChange={(o) => !o && setShowReuploadModal(null)}
                customerId={showReuploadModal?.customerId || null}
                filename={showReuploadModal?.filename || null}
                fileRefs={showReuploadModal?.fileRefs || null}
            />
        </DashboardShell>
    );
}

function ContractsTab({ customerId, customerName }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRental, setShowRental] = useState(false);
    const [showAch, setShowAch] = useState(false);
    const [attachFor, setAttachFor] = useState(null); // contract object whose orphan rental is being attached
    const navigate = useNavigate();
    const location = useLocation();
    // Phase TASK28E-REASSIGN-PREFILL — when the URL carries a
    // ?reassign_from=<source_rental_id> query param, auto-open the
    // "Create rental contract" dialog with that ID so the dialog
    // fetches + hydrates the source rental's reusable fields.
    const prefillFromRentalId = new URLSearchParams(location.search).get("reassign_from");
    useEffect(() => {
        if (prefillFromRentalId) {
            setShowRental(true);
        }
    }, [prefillFromRentalId]);
    // Task CONTRACT-PERM — gate the two creation buttons on the same permission
    // the backend already enforces (routers/contracts.py POST
    // /admin/customers/{customer_id}/contracts → require_permission("Contracts",
    // "Create / draft")). Both rental_agreement and ach_authorization dispatch
    // through the same endpoint, so a single check gates both buttons. While
    // the matrix is still loading (``ready === false``) we hide the buttons —
    // avoids a flash-then-hide for lower roles. ``super_admin`` short-circuits
    // to ``true`` inside the hook, so they never see the loading window.
    const { ready, hasPermission } = usePermission();
    const canCreateContract = ready && hasPermission("Contracts", "Create / draft");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/customers/${customerId}/contracts`);
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

    return (
        <div data-testid="customer-contracts-tab">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Contracts</h3>
                <div className="flex items-center gap-2">
                    {canCreateContract && (
                        <>
                            <Button size="sm" onClick={() => setShowRental(true)} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                data-testid="customer-new-rental-contract">
                                <FileText size={14} /> + Create rental contract
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setShowAch(true)}
                                data-testid="customer-new-ach-contract">
                                + Create ACH authorization
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
                <Card className="border-dashed border-2 border-slate-200 bg-slate-50/60" data-testid="customer-contracts-empty">
                    <CardContent className="py-10 px-6 sm:px-10 text-center">
                        <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                            <FileText size={20} />
                        </div>
                        <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No contracts yet.</h3>
                        <p className="mt-2 max-w-sm mx-auto text-sm text-slate-600">
                            Create a rental contract to bundle the equipment, paperwork, and (optionally) an ACH authorization in one signing session.
                        </p>
                        {canCreateContract && (
                            <div className="mt-4 flex items-center justify-center gap-2">
                                <Button size="sm" onClick={() => setShowRental(true)} className="bg-blue-900 hover:bg-blue-800"
                                    data-testid="customer-empty-create-rental">
                                    + Create rental contract
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setShowAch(true)} data-testid="customer-empty-create-ach">
                                    + Create ACH authorization
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
            {!loading && items.length > 0 && (
                <div className="space-y-3">
                    {items.filter((k) => k.needs_rental).map((k) => (
                        <div key={`banner-${k.id}`}
                            data-testid={`needs-rental-banner-${k.id}`}
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-amber-900">
                                <AlertTriangle size={14} />
                                <span><strong>{k.title}</strong> is missing rental equipment. Attach a rental to send it for signature.</span>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setAttachFor(k)}
                                data-testid={`attach-rental-${k.id}`}>Attach rental</Button>
                        </div>
                    ))}
                    <table className="min-w-full text-sm">
                        <thead className="text-xs text-slate-500 uppercase">
                            <tr>
                                <th className="text-left py-2">Title</th>
                                <th className="text-left py-2">Type</th>
                                <th className="text-left py-2">Status</th>
                                <th className="text-left py-2">Created</th>
                                <th className="text-left py-2">Signed</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((k) => (
                                <tr key={k.id} data-testid={`customer-contract-row-${k.id}`}>
                                    <td className="py-2 text-slate-900 font-medium">
                                        {k.title}
                                        {k.needs_rental && <span className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-900 ring-1 ring-amber-200">Needs rental</span>}
                                    </td>
                                    <td className="py-2 capitalize text-slate-700">{k.type === "rental_agreement" ? "Rental" : "ACH"}</td>
                                    <td className="py-2">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${CONTRACT_STATUS_COLOR[k.status]}`}>
                                            {CONTRACT_STATUS_LABEL[k.status] || k.status}
                                        </span>
                                        {/* Phase AE — draft contracts created via the
                                            assign-equipment handoff carry a marker we
                                            surface as a "Pending Contract" badge so
                                            admins know this row is awaiting the
                                            template-selection / generation step. */}
                                        {k.status === "draft" && k.ae_created_from_submission_id && (
                                            <span
                                                data-testid={`pending-contract-badge-${k.id}`}
                                                className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-900 ring-1 ring-blue-200"
                                            >
                                                Pending Contract
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2 text-slate-600">{fmtDate(k.created_at)}</td>
                                    <td className="py-2 text-slate-600">{fmtDate(k.admin_signed_at || k.customer_signed_at)}</td>
                                    <td className="py-2 text-right">
                                        <Button asChild size="sm" variant="outline">
                                            <Link to={`/admin/contracts/${k.id}`} data-testid={`customer-contract-open-${k.id}`}>Open</Link>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateRentalContractDialog
                open={showRental}
                onOpenChange={setShowRental}
                customerId={customerId}
                customerName={customerName}
                prefillFromRentalId={prefillFromRentalId}
                onCreated={(contract) => {
                    setShowRental(false);
                    navigate(`/admin/contracts/${contract.id}`);
                }}
            />

            <CreateAchContractDialog
                open={showAch}
                onOpenChange={setShowAch}
                customerId={customerId}
                customerName={customerName}
                onCreated={(contract) => {
                    setShowAch(false);
                    navigate(`/admin/contracts/${contract.id}`);
                }}
            />

            <AttachRentalDialog
                contract={attachFor}
                onClose={() => setAttachFor(null)}
                onAttached={() => { setAttachFor(null); load(); }}
            />
        </div>
    );
}

function AttachRentalDialog({ contract, onClose, onAttached }) {
    const [rental, setRental] = useState({
        equipment_type: "reefer", unit_number: "", start_date: "",
        make: "", model: "", year: "", vin: "", plate: "", end_date: "",
        daily_rate: "", monthly_rate: "", minimum_term_months: "",
        billing_period: "", deposit: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    // Phase M.1 — conflict banner state, mirrored from
    // ``UnitNumberConflictBanner`` via ``onStateChange``.
    const [conflictState, setConflictState] = useState({
        inConflict: false, canForceSubmit: false, overrideReason: "",
    });
    useEffect(() => {
        if (contract) {
            setRental({
                equipment_type: "reefer", unit_number: "", start_date: "",
                make: "", model: "", year: "", vin: "", plate: "", end_date: "",
                daily_rate: "", monthly_rate: "", minimum_term_months: "",
                billing_period: "", deposit: "", notes: "",
            });
            setConflictState({
                inConflict: false, canForceSubmit: false, overrideReason: "",
            });
        }
    }, [contract]);
    if (!contract) return null;
    const updateRental = (k, v) => setRental((r) => ({ ...r, [k]: v }));
    const submit = async () => {
        if (!rental.unit_number.trim()) { toast.error("Unit number is required"); return; }
        if (!rental.start_date) { toast.error("Start date is required"); return; }
        // Phase M.1 — force-path guard: if the banner is up but the
        // override_reason is missing/too-short, block the submit
        // client-side (server would 400 anyway).
        if (conflictState.inConflict && !conflictState.canForceSubmit) {
            toast.error("Enter an override reason (≥10 chars) to force this reassignment.");
            return;
        }
        const payload = { ...rental };
        ["year", "daily_rate", "monthly_rate", "deposit", "minimum_term_months"].forEach((k) => {
            if (payload[k] === "" || payload[k] == null) delete payload[k];
            else payload[k] = Number(payload[k]);
        });
        Object.keys(payload).forEach((k) => {
            if (payload[k] === "" || payload[k] == null) delete payload[k];
        });
        if (!payload.billing_period) delete payload.billing_period;
        // M.1 force override — include reason in body, ?force in URL.
        const url = conflictState.inConflict
            ? `/admin/contracts/${contract.id}/rental?force=true`
            : `/admin/contracts/${contract.id}/rental`;
        if (conflictState.inConflict) {
            payload.override_reason = conflictState.overrideReason;
        }
        setBusy(true);
        try {
            await api.post(url, payload);
            toast.success(conflictState.inConflict
                ? "Rental attached (equipment reassigned)"
                : "Rental attached");
            onAttached?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={Boolean(contract)} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="attach-rental-dialog">
                <DialogHeader>
                    <DialogTitle>Attach rental to "{contract.title}"</DialogTitle>
                    <DialogDescription>This contract was drafted before rentals became required. Fill in the equipment so it can be sent for signature.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5 col-span-2">
                        <Label>Equipment type</Label>
                        <select value={rental.equipment_type} onChange={(e) => updateRental("equipment_type", e.target.value)}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="attach-eq-type">
                            <option value="reefer">Reefer</option>
                            <option value="dry_van">Dry Van</option>
                            <option value="conestoga">Conestoga</option>
                            <option value="flatbed">Flatbed</option>
                            <option value="step_deck">Step Deck</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Unit number *</Label>
                        <Input value={rental.unit_number} onChange={(e) => updateRental("unit_number", e.target.value)} data-testid="attach-unit" />
                    </div>
                    {/* Phase M.1 — double-assignment guard banner. Positioned
                        below the unit_number input; spans both columns so
                        the warning + override_reason textarea have room. */}
                    <div className="col-span-2">
                        <UnitNumberConflictBanner
                            unitNumber={rental.unit_number}
                            onStateChange={setConflictState}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Start date *</Label>
                        <Input type="date" value={rental.start_date} onChange={(e) => updateRental("start_date", e.target.value)} data-testid="attach-start" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || (conflictState.inConflict && !conflictState.canForceSubmit)}
                        className={conflictState.inConflict
                            ? "bg-red-600 hover:bg-red-700 text-white"
                            : "bg-blue-900 hover:bg-blue-800"
                        }
                        data-testid="attach-rental-submit"
                    >
                        {busy
                            ? "Attaching…"
                            : conflictState.inConflict
                                ? "Force Reassign"
                                : "Attach rental"
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const RENTAL_STATUS_COLOR = {
    pending_contract: "bg-slate-100 text-slate-700",
    contract_signed: "bg-blue-50 text-blue-900",
    coi_pending: "bg-amber-50 text-amber-900",
    coi_received: "bg-violet-50 text-violet-800",
    coi_approved: "bg-emerald-50 text-emerald-800",
    active: "bg-emerald-100 text-emerald-900",
    ended: "bg-slate-200 text-slate-700",
    terminated: "bg-red-50 text-red-700",
    scheduled: "bg-blue-50 text-blue-800",
    imported: "bg-violet-50 text-violet-800",
};

const RENTAL_STATUS_LABEL = {
    pending_contract: "Pending contract",
    contract_signed: "Contract signed",
    coi_pending: "Waiting for COI",
    coi_received: "COI received",
    coi_approved: "COI approved",
    active: "Active",
    ended: "Ended",
    terminated: "Terminated",
    scheduled: "Scheduled",
    imported: "Imported",
};

function fmtDay(s) { if (!s) return "—"; return s.slice(0, 10); }

function RentalsTab({ customerId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showImport, setShowImport] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/customers/${customerId}/rentals`);
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

    return (
        <div data-testid="customer-rentals-tab">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Rentals</h3>
                <div className="flex items-center gap-3">
                    <p className="text-xs text-slate-500 hidden sm:block">Auto-created from contracts. Or import a paper rental →</p>
                    <Button size="sm" onClick={() => setShowImport(true)} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="customer-import-rental"><FileText size={14} /> + Add current rental</Button>
                </div>
            </div>
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="customer-rentals-empty">No rentals yet for this customer.</p>
            )}
            {!loading && items.length > 0 && (
                <table className="min-w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left py-2">Equipment</th>
                            <th className="text-left py-2">Unit</th>
                            <th className="text-left py-2">Status</th>
                            <th className="text-left py-2">Start</th>
                            <th className="text-left py-2">End</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {items.map((r) => (
                            <tr key={r.id} data-testid={`customer-rental-row-${r.id}`}>
                                <td className="py-2 capitalize text-slate-700">
                                    {(r.equipment_type || "").replace("_", " ") || "—"}
                                    {formatRateChip(r) && (
                                        <div className="text-[11px] text-slate-500 normal-case mt-0.5"
                                            data-testid={`customer-rental-rate-${r.id}`}>{formatRateChip(r)}</div>
                                    )}
                                </td>
                                <td className="py-2 text-slate-700">{r.unit_number || "—"}</td>
                                <td className="py-2">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${RENTAL_STATUS_COLOR[r.status] || "bg-slate-100"}`}>
                                            {RENTAL_STATUS_LABEL[r.status] || r.status}
                                        </span>
                                        {r.imported && (
                                            <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200"
                                                data-testid={`customer-rental-imported-${r.id}`}>Imported</span>
                                        )}
                                    </div>
                                </td>
                                <td className="py-2 text-slate-600">{fmtDay(r.start_date)}</td>
                                <td className="py-2 text-slate-600">{fmtDay(r.end_date) || "open"}</td>
                                <td className="py-2 text-right">
                                    <Button asChild size="sm" variant="outline">
                                        <Link to={`/admin/rentals/${r.id}?from=customer`} data-testid={`customer-rental-open-${r.id}`}>Open</Link>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <ImportRentalDialog
                customerId={customerId}
                open={showImport}
                onOpenChange={setShowImport}
                onCreated={() => load()}
            />
        </div>
    );
}



// -------------------------------------------------------------------------
// Phase K.1 — Consolidated Billing (statements) — per-customer tab
// -------------------------------------------------------------------------

const STATEMENT_STATUS_STYLE = {
    draft: "bg-slate-100 text-slate-700",
    sent: "bg-blue-50 text-blue-900",
    processing: "bg-amber-50 text-amber-900",
    paid: "bg-emerald-100 text-emerald-900",
    failed: "bg-red-50 text-red-700",
    void: "bg-slate-200 text-slate-700",
};

function fmtStatementMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

function CustomerStatementsTab({ customerId, customer, isSuperAdmin, isAdmin, onCustomerRefresh }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/admin/customers/${customerId}/statements`,
                { params: { limit: 100 } }
            );
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, [customerId]);

    return (
        <div className="space-y-4" data-testid="customer-statements-tab">
            {/* Phase K.1c-fe — Billing mode card (super-admin edit) */}
            {customer && (
                <CustomerBillingModeCard
                    customer={customer}
                    isSuperAdmin={!!isSuperAdmin}
                    isAdmin={!!isAdmin}
                    onSaved={() => { if (onCustomerRefresh) onCustomerRefresh(); }}
                />
            )}

            <div className="flex items-center justify-between">
                <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                    Consolidated statements
                </h3>
                <Button
                    size="sm"
                    onClick={() => setShowCreate(true)}
                    data-testid="customer-create-statement-btn"
                >
                    <FileText className="h-4 w-4 mr-1" /> Create statement
                </Button>
            </div>

            {loading && items.length === 0 && (
                <div className="text-sm text-slate-500 py-6">Loading…</div>
            )}
            {!loading && items.length === 0 && (
                <div className="text-sm text-slate-500 py-6">
                    No statements yet.
                </div>
            )}

            {items.length > 0 && (
                <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-slate-500 border-b">
                        <tr>
                            <th className="text-left py-2">Statement</th>
                            <th className="text-left py-2">Period</th>
                            <th className="text-right py-2">Total</th>
                            <th className="text-left py-2">Status</th>
                            <th className="text-left py-2">Invoices</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.id} className="border-b hover:bg-slate-50"
                                data-testid={`customer-statement-row-${s.id}`}>
                                <td className="py-2 text-slate-800"
                                    data-testid={`customer-statement-row-number-${s.id}`}
                                    title={s.id}>
                                    {formatStatementIdentifier(s)}
                                </td>
                                <td className="py-2 text-slate-600 text-xs">
                                    {formatStatementPeriod(
                                        s.period_start_date || s.period_start,
                                        s.period_end_date || s.period_end,
                                    )}
                                </td>
                                <td className="py-2 text-right font-medium">
                                    {fmtStatementMoney(s.total_cents)}
                                </td>
                                <td className="py-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        STATEMENT_STATUS_STYLE[s.status] || "bg-slate-100"
                                    }`}>
                                        {s.status}
                                    </span>
                                </td>
                                <td className="py-2 text-slate-600">
                                    {(s.invoice_ids || []).length}
                                </td>
                                <td className="text-right">
                                    <Button asChild size="sm" variant="outline">
                                        <Link to={`/admin/statements/${s.id}`}
                                              data-testid={`customer-statement-open-${s.id}`}>
                                            Open
                                        </Link>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <CreateStatementModal
                customerId={customerId}
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={() => reload()}
            />
        </div>
    );
}

